import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "../../components/ui/button";
import SelectField from "../../components/ui/SelectField";
import { toast } from "react-toastify";
import { CheckCircle, AlertTriangle, Search, Plus } from "lucide-react";
import CaratulasTodasEspecies from "./CaratulasTodasEspecies";

const CaratulasEspeciePage = () => {
  const [especies, setEspecies] = useState([]);
  const [examenes, setExamenes] = useState([]);
  const [equipos, setEquipos] = useState([]);
  const [reactivos, setReactivos] = useState([]);

  const [selectedEspecie, setSelectedEspecie] = useState("");
  const [selectedExamen, setSelectedExamen] = useState("");
  const [selectedEquipo, setSelectedEquipo] = useState("");
  const [selectedReactivo, setSelectedReactivo] = useState("");

  const [loading, setLoading] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  const token = localStorage.getItem("token");
  const API_URL = process.env.REACT_APP_API_URL + "/util";

  const isSelectionComplete = selectedEspecie && selectedExamen && selectedEquipo && selectedReactivo;

  const fetchCombos = async () => {
    try {
      const [especiesRes, examenesRes, equiposRes, reactivosRes] = await Promise.all([
        axios.get(`${API_URL}/especies`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/examenes_caratula`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/equipos`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/reactivos`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setEspecies(especiesRes.data || []);
      setExamenes(examenesRes.data || []);
      setEquipos(equiposRes.data || []);
      setReactivos(reactivosRes.data || []);
    } catch (error) {
      console.error("Error cargando catálogos de carátulas:", error);
      toast.error("No se pudieron cargar los catálogos");
    }
  };

  useEffect(() => { fetchCombos(); }, []); // eslint-disable-line

  const handleVerify = async () => {
    if (!isSelectionComplete) { toast.error("Debes seleccionar especie, examen, equipo y reactivo"); return; }
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/caratulas_especie/verify`, {
        params: { especie: selectedEspecie, examen: selectedExamen, equipo: selectedEquipo, reactivo: selectedReactivo },
        headers: { Authorization: `Bearer ${token}` },
      });
      setVerifyResult(data);
      if (data.completa) toast.success(data.message);
      else toast.warning(data.message);
    } catch (error) {
      toast.error(error.response?.data?.error || "Error al verificar carátulas");
      setVerifyResult(null);
    } finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!isSelectionComplete) { toast.error("Debes seleccionar especie, examen, equipo y reactivo"); return; }
    try {
      setLoading(true);
      const { data } = await axios.post(`${API_URL}/caratulas_especie/create`, {
        especie: selectedEspecie, examen: selectedExamen, equipo: selectedEquipo, reactivo: selectedReactivo,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(data.message || "Carátulas construidas correctamente");
      await handleVerify();
    } catch (error) {
      toast.error(error.response?.data?.error || "No se pudieron construir las carátulas");
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-5">
      <div>
        <p className="text-sm text-gray-600">
          Construye y verifica carátulas por especie para evitar sobrecarga de datos por todas las especies al mismo tiempo.
        </p>
      </div>

      <div className="bg-brand-50 border border-brand-100 rounded-xl p-4">
        <p className="text-xs font-semibold text-brand-700 uppercase tracking-wider mb-3">Seleccionar parámetros</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <SelectField name="especie" value={selectedEspecie} onChange={(e) => setSelectedEspecie(e.target.value)}
            disabled={loading} placeholder="Selecciona especie" options={especies} valueKey="codigo"
            displayFn={(es) => `${es.codigo} - ${es.detalle}`} />
          <SelectField name="examen" value={selectedExamen} onChange={(e) => setSelectedExamen(e.target.value)}
            disabled={loading} placeholder="Selecciona examen" options={examenes} valueKey="examen_cod"
            displayFn={(ex) => `${ex.examen_cod} - ${ex.nombre}`} />
          <SelectField name="equipo" value={selectedEquipo} onChange={(e) => setSelectedEquipo(e.target.value)}
            disabled={loading} placeholder="Selecciona equipo" options={equipos} valueKey="equipo_cod"
            displayFn={(eq) => `${eq.equipo_cod} - ${eq.equipo_desc}`} />
          <SelectField name="reactivo" value={selectedReactivo} onChange={(e) => setSelectedReactivo(e.target.value)}
            disabled={loading} placeholder="Selecciona reactivo" options={reactivos} valueKey="reactivo_cod"
            displayFn={(re) => `${re.reactivo_cod} - ${re.reactivo_deta}`} />
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={handleVerify} disabled={loading} className="gap-2">
            <Search className="h-4 w-4" /> {loading ? "Verificando..." : "Verificar"}
          </Button>
          <Button onClick={handleCreate} disabled={loading} className="gap-2">
            <Plus className="h-4 w-4" /> {loading ? "Construyendo..." : "Construir carátula"}
          </Button>
        </div>
      </div>

      {verifyResult && (
        <div className={`rounded-xl border p-4 ${verifyResult.completa ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
          <div className="flex items-center gap-2 mb-3">
            {verifyResult.completa
              ? <CheckCircle className="h-5 w-5 text-emerald-600" />
              : <AlertTriangle className="h-5 w-5 text-amber-600" />}
            <p className={`font-semibold text-sm ${verifyResult.completa ? "text-emerald-700" : "text-amber-700"}`}>
              {verifyResult.completa ? "Carátula completa" : "Carátula incompleta"}
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            {[
              ["Analitos base", verifyResult.totalAnalitos],
              ["Razas activas", verifyResult.totalRazas],
              ["Registros esperados", verifyResult.totalEsperado],
              ["Registros actuales", verifyResult.totalActual],
            ].map(([label, value]) => (
              <div key={label} className="bg-white rounded-lg p-3 border border-white/50">
                <p className="text-xs text-gray-500 mb-1">{label}</p>
                <p className="text-xl font-bold text-gray-800">{value}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <hr className="border-gray-200" />

      <CaratulasTodasEspecies
        especies={especies}
        examenes={examenes}
        equipos={equipos}
        reactivos={reactivos}
      />
    </div>
  );
};

export default CaratulasEspeciePage;
